import type React from "react";
import { useEffect } from "react";
import { useWorkflowState } from "../hooks/useWorkflowState";
import { AnswerDisplay } from "./AnswerDisplay";
import { ChatInterface } from "./ChatInterface";
import { GlossyButton } from "./GlossyButton";

export interface RequirementsFormData {
  industry: string;
  businessProcesses: string[];
  companySize: string;
  specificNeeds: string[];
}

interface RequirementsFormProps {
  onSubmit: (data: RequirementsFormData) => void;
  loading?: boolean;
  isResultsMode?: boolean;
  initialData?: RequirementsFormData | null;
  onLiveUpdate?: (formData: RequirementsFormData) => Promise<void>;
  showLivePreview?: boolean;
}

const COMPANY_SIZES = [
  { value: "1-3", label: "1-3 employees" },
  { value: "4-10", label: "4-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "200+", label: "200+ employees" },
];

export const RequirementsForm: React.FC<RequirementsFormProps> = ({
  onSubmit,
  loading = false,
  isResultsMode = false,
  initialData = null,
  onLiveUpdate,
  showLivePreview = false,
}) => {
  // Use the abstraction layer hook
  const {
    currentStep,
    formData,
    hasChanges,
    isFormComplete,
    start,
    setIndustry,
    setCompanySize,
    addBusinessProcess,
    removeBusinessProcess,
    addSpecificNeed,
    removeSpecificNeed,
    chatHistory,
    addChatMessage,
  } = useWorkflowState(initialData);

  // Determine if workflow has started
  const isStarted = currentStep !== "welcome";

  // Initialize with existing data in results mode
  useEffect(() => {
    if (isResultsMode && initialData && currentStep === "welcome") {
      start(); // Auto-start in results mode
    }
  }, [isResultsMode, initialData, currentStep, start]);

  // Optional live update callback (used by split-screen preview)
  useEffect(() => {
    if (typeof onLiveUpdate === "function") {
      try {
        // Only invoke live updates when minimal data is present
        if (formData.industry && formData.companySize) {
          onLiveUpdate(formData).catch((e) => console.debug("onLiveUpdate failed", e));
        }
      } catch (err) {
        console.debug("onLiveUpdate error", err);
      }
    }
  }, [formData, onLiveUpdate]);

  // Handle form submission
  const handleSubmit = async () => {
    // Simple validation: require industry and company size (business processes optional per updated rules)
    if (!formData.industry || !formData.companySize) {
      return;
    }

    // Call parent's onSubmit with the current form data
    onSubmit(formData);
  };

  // Edit handlers to clear specific fields (go back to that question)
  const handleEditIndustry = () => {
    setIndustry("");
  };

  const handleEditCompanySize = () => {
    setCompanySize("");
  };

  // Determine which button to show
  const showUpdateButton = isResultsMode && hasChanges;
  const showNextButton = !isResultsMode && isFormComplete && isStarted;

  // mark optional prop as used to satisfy strict TS checks
  void showLivePreview;

  return (
    <div className="requirements-layout">
      {/* Left: Summary column (shows jumped-out answers in results mode) */}
      <aside
        className={`summary-column ${isResultsMode ? "visible" : "hidden"}`}
        aria-hidden={!isResultsMode}
      >
        {isResultsMode && (
          <div className="space-y-4 mb-4">
            {formData.industry && (
              <AnswerDisplay
                label="Industry"
                value={formData.industry}
                onEdit={handleEditIndustry}
                editable
              />
            )}

            {formData.companySize && (
              <AnswerDisplay
                label="Company Size"
                value={
                  COMPANY_SIZES.find((s) => s.value === formData.companySize)?.label ||
                  formData.companySize
                }
                onEdit={handleEditCompanySize}
                editable
              />
            )}

            {formData.businessProcesses.length > 0 && (
              <AnswerDisplay
                label="Business Processes"
                value={formData.businessProcesses}
                onRemoveItem={removeBusinessProcess}
                editable
                maxVisible={6}
              />
            )}

            {formData.specificNeeds.length > 0 && (
              <AnswerDisplay
                label="Specific Needs"
                value={formData.specificNeeds}
                onRemoveItem={removeSpecificNeed}
                editable
              />
            )}
          </div>
        )}
      </aside>

      {/* Right: Chat column */}
      <div className="chat-column">
        <ChatInterface
          currentStep={
            currentStep as
              | "welcome"
              | "industry"
              | "companySize"
              | "businessProcesses"
              | "specificNeeds"
          }
          industry={formData.industry}
          companySize={formData.companySize}
          businessProcesses={formData.businessProcesses}
          specificNeeds={formData.specificNeeds}
          onStart={start}
          onIndustrySelect={setIndustry}
          onCompanySizeSelect={setCompanySize}
          onProcessAdd={addBusinessProcess}
          onProcessRemove={removeBusinessProcess}
          onNeedAdd={addSpecificNeed}
          chatHistory={chatHistory}
          addChatMessage={addChatMessage}
          onNext={handleSubmit}
          showNextButton={showNextButton || showUpdateButton}
          loading={loading}
        />

        {/* Action Button - Only for results mode (update), not for businessProcesses */}
        {showUpdateButton && (
          <div className="flex justify-center pt-4">
            <GlossyButton
              label={loading ? "Updating..." : "UPDATE"}
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
};
